/**
 * BusinessSearchCard — Shared visual shell for business search entry points.
 *
 * Pure presentational component. Renders hero text, an elevated card with
 * gradient accent, generic tabs, search input area (children), a secondary
 * slot with animated reveal, error display, gradient CTA, and a footer slot.
 *
 * Used by:
 *   - Creative Studio SearchStep (Google Maps + Website URL + AdvertiserSelector)
 *   - Self-serve MediaPlanSearch (Google Maps + Manual Entry + business preview)
 */
import React from 'react';
import { Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export default function BusinessSearchCard({
  // Hero
  title,
  subtitle,

  // Tabs
  tabs,
  activeTab,
  onTabChange,

  // Search content
  children,

  // Secondary slot (animated reveal)
  secondarySlot = null,
  showSecondarySlot,

  // Error
  error = null,

  // CTA
  ctaLabel,
  ctaLoadingLabel = 'Loading...',
  ctaDisabled = false,
  isLoading = false,
  onSubmit,
  continuationLabel = null,
  disabledTooltip = null,

  // Footer
  footerSlot = null,

  // Layout
  className,
}) {
  // Default showSecondarySlot to whether a slot was provided
  const shouldShowSlot = showSecondarySlot !== undefined ? showSecondarySlot : !!secondarySlot;

  return (
    <div className={cn(
      'flex flex-col items-center justify-center min-h-0 sm:min-h-[80vh] w-full max-w-2xl mx-auto px-4 py-8 sm:py-0 animate-in fade-in zoom-in-95 duration-500',
      className,
    )}>
      {/* Hero */}
      <div className="text-center mb-6 space-y-3">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-md mx-auto leading-relaxed">
          {subtitle}
        </p>
      </div>

      {/* Card */}
      <div className="w-full bg-card rounded-2xl shadow-md border border-border/50 transition-all duration-300">
        <div className="p-6 md:p-8 space-y-6">
          {/* Tabs */}
          {tabs && tabs.length > 0 && (
            <div className="space-y-4">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold ml-1">
                Find Business
              </Label>
              <div className="flex rounded-lg border bg-muted/30 p-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => onTabChange(tab.id)}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-all',
                        activeTab === tab.id
                          ? 'bg-white text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Icon className="size-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search content (rendered by parent) */}
          {children}

          {/* Secondary slot with animated reveal */}
          {secondarySlot && (
            <div className={cn(
              'grid transition-all duration-500 ease-in-out overflow-hidden',
              shouldShowSlot ? 'grid-rows-[1fr] opacity-100 pt-2' : 'grid-rows-[0fr] opacity-0',
            )}>
              <div className="min-h-0">
                {secondarySlot}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50/50 text-red-600 rounded-xl border border-red-100 text-sm flex items-center gap-2 animate-in fade-in">
              <div className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
              {error}
            </div>
          )}

          {/* CTA Button */}
          <div className="pt-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block w-full">
                    <Button
                      size="lg"
                      onClick={onSubmit}
                      disabled={ctaDisabled || isLoading}
                      className={cn(
                        'w-full h-14 text-lg font-semibold rounded-xl shadow-sm transition-all duration-300',
                        !(ctaDisabled || isLoading) && 'bg-coral hover:bg-coral-hover text-white hover:shadow-coral/25 hover:scale-[1.02]',
                      )}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          {ctaLoadingLabel}
                        </>
                      ) : continuationLabel ? (
                        <span className="flex items-center">
                          {continuationLabel} <ArrowRight className="ml-2 h-5 w-5" />
                        </span>
                      ) : (
                        ctaLabel
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {disabledTooltip && (
                  <TooltipContent side="bottom">
                    {disabledTooltip}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Footer slot */}
          {footerSlot}
        </div>
      </div>
    </div>
  );
}
